import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Eye, EyeOff, Plus, Redo2, Trash2, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CHORD_KEYS } from '@/chordpro/transpose';
import ChordProRenderer from './ChordProRenderer';
import { parseChordPro } from '@/chordpro/parser';
import {
  parseStructuredSong,
  removeLineChord,
  serializeStructuredSong,
  updateLineChord,
} from '@/chordpro/structuredSongAdapter';
import { utf16IndexToCodePointOffset } from '@/chordpro/structuredSong';

const SECTION_LABELS = {
  body: 'Tekst', verse: 'Couplet', chorus: 'Refrein', bridge: 'Bridge', intro: 'Intro',
  outro: 'Outro', repeat: 'Tussenspel', tab: 'Tab', grid: 'Akkoorden',
};

const HighlightedSource = ({ source }) => <>{source.split(/(\{[^}\n]*}|\[[^\]\n]+])/g).map((part, index) => {
  if (part.startsWith('{')) return <span key={index} className="chordpro-token-directive">{part}</span>;
  if (part.startsWith('[')) return <span key={index} className="chordpro-token-chord">{part}</span>;
  return <React.Fragment key={index}>{part}</React.Fragment>;
})}</>;

const SECTION_TYPES = [
  { value: 'verse', label: 'Couplet' },
  { value: 'chorus', label: 'Refrein' },
  { value: 'bridge', label: 'Bridge' },
  { value: 'intro', label: 'Intro' },
  { value: 'outro', label: 'Outro' },
  { value: 'repeat', label: 'Tussenspel / repeat' },
  { value: 'tab', label: 'Tab' },
];

const CHORD_QUALITIES = [
  { value: 'maj', label: 'Majeur' },
  { value: 'm', label: 'Mineur' },
  { value: '7', label: '7' },
  { value: 'maj7', label: 'maj7' },
  { value: 'm7', label: 'm7' },
  { value: 'sus2', label: 'sus2' },
  { value: 'sus4', label: 'sus4' },
  { value: 'dim', label: 'dim' },
  { value: 'aug', label: 'aug' },
  { value: 'add9', label: 'add9' },
  { value: '6', label: '6' },
  { value: '9', label: '9' },
];

const StructuredSongEditor = ({ result, source, onChange }) => {
  const [song, setSong] = useState(result.song);
  const [activeSectionKey, setActiveSectionKey] = useState(result.song.arrangements[0]?.sections[0]?.stable_key || null);
  const [editingChord, setEditingChord] = useState(null);
  const sectionRefs = useRef(new Map());
  const lastSerializedRef = useRef(null);
  const arrangement = song.arrangements[0];
  const sections = arrangement?.sections || [];

  useEffect(() => {
    if (lastSerializedRef.current === source) {
      lastSerializedRef.current = null;
      return;
    }
    setSong(result.song);
    setActiveSectionKey((current) => result.song.arrangements[0]?.sections.some((section) => section.stable_key === current)
      ? current : result.song.arrangements[0]?.sections[0]?.stable_key || null);
  }, [result.song, source]);

  const commitSong = (nextSong) => {
    const nextSource = serializeStructuredSong(nextSong);
    setSong(nextSong);
    lastSerializedRef.current = nextSource;
    onChange(nextSource);
  };

  const updateMetadata = (name, value) => commitSong({
    ...song,
    metadata: { ...song.metadata, [name]: value },
  });

  const updateSection = (sectionKey, changes) => commitSong({
    ...song,
    arrangements: [{ ...arrangement, sections: sections.map((section) => section.stable_key === sectionKey ? { ...section, ...changes } : section) }],
  });

  const updateLine = (sectionKey, lineKey, changes) => commitSong({
    ...song,
    arrangements: [{ ...arrangement, sections: sections.map((section) => section.stable_key !== sectionKey ? section : {
      ...section, lines: section.lines.map((line) => line.stable_key === lineKey ? { ...line, ...changes } : line),
    }) }],
  });

  const addSection = () => {
    const order = sections.length;
    const newSection = { stable_key: `section-local-${Date.now()}-${order}`, type: 'verse', label: 'Couplet', order, lines: [], preserved_fragments: [] };
    commitSong({ ...song, arrangements: [{ ...arrangement, sections: [...sections, newSection] }] });
    setActiveSectionKey(newSection.stable_key);
  };

  const removeSection = (sectionKey) => commitSong({ ...song, arrangements: [{ ...arrangement, sections: sections.filter((section) => section.stable_key !== sectionKey).map((section, order) => ({ ...section, order })) }] });

  const moveSection = (sectionKey, direction) => {
    const index = sections.findIndex((section) => section.stable_key === sectionKey);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= sections.length) return;
    const next = [...sections];
    [next[index], next[target]] = [next[target], next[index]];
    commitSong({ ...song, arrangements: [{ ...arrangement, sections: next.map((section, order) => ({ ...section, order })) }] });
  };

  const focusSection = (sectionKey) => {
    setActiveSectionKey(sectionKey);
    const element = sectionRefs.current.get(sectionKey);
    element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    element?.focus({ preventScroll: true });
  };

  const addChordAtCursor = (section, line, chord) => {
    if (!chord.trim()) return;
    const textarea = document.activeElement?.dataset?.lineKey === line.stable_key ? document.activeElement : null;
    const utf16Position = textarea ? textarea.selectionStart : line.text.length;
    const position = utf16IndexToCodePointOffset(line.text, utf16Position);
    updateLine(section.stable_key, line.stable_key, { chords: [...line.chords, { position, name: chord.trim() }].sort((a, b) => a.position - b.position) });
    setEditingChord(null);
  };

  return <div className="structured-song-editor">
    <div className="structured-song-editor-intro">
      <div><p className="font-semibold text-slate-900">Visuele liedbewerker</p><p className="text-xs text-slate-500">Bewerk elk onderdeel afzonderlijk. ChordPro-opmaak wordt automatisch bijgehouden.</p></div>
      <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">{sections.length} onderdelen</span>
    </div>
    <nav className="structured-song-section-nav" aria-label="Liedonderdelen">
      {sections.map((section) => <button type="button" key={section.stable_key} className={activeSectionKey === section.stable_key ? 'is-active' : ''} onClick={() => focusSection(section.stable_key)}>{section.label || section.type}</button>)}
    </nav>
    <div className="structured-song-metadata">
      <label><span>Titel</span><input value={song.metadata.title || ''} onChange={(event) => updateMetadata('title', event.target.value)} placeholder="Naamloos lied" /></label>
      <label><span>Artiest / uitvoerder</span><input value={song.metadata.artist || ''} onChange={(event) => updateMetadata('artist', event.target.value)} placeholder="Nog niet ingevuld" /></label>
      <label><span>Copyright</span><input value={song.metadata.copyright || ''} onChange={(event) => updateMetadata('copyright', event.target.value)} placeholder="Nog niet ingevuld" /></label>
    </div>
    <div className="structured-song-sections">
      {sections.map((section, index) => <article tabIndex="-1" data-section-key={section.stable_key} ref={(element) => { if (element) sectionRefs.current.set(section.stable_key, element); else sectionRefs.current.delete(section.stable_key); }} className={`structured-song-section ${activeSectionKey === section.stable_key ? 'is-active' : ''}`} key={section.stable_key}>
        <header>
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <span className="songwriting-section-number">{index + 1}</span>
            <select aria-label="Type liedonderdeel" value={section.type} onChange={(event) => updateSection(section.stable_key, { type: event.target.value })}>
              {Object.entries(SECTION_LABELS).filter(([value]) => value !== 'body').map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            <input aria-label="Naam liedonderdeel" value={section.label || ''} onChange={(event) => updateSection(section.stable_key, { label: event.target.value })} placeholder={SECTION_LABELS[section.type] || 'Onderdeel'} />
          </div>
          <div className="flex shrink-0 items-center gap-0.5">
            <Button type="button" variant="ghost" size="icon" disabled={index === 0} onClick={() => moveSection(section.stable_key, -1)} title="Omhoog"><ChevronUp className="h-4 w-4" /></Button>
            <Button type="button" variant="ghost" size="icon" disabled={index === sections.length - 1} onClick={() => moveSection(section.stable_key, 1)} title="Omlaag"><ChevronDown className="h-4 w-4" /></Button>
            <Button type="button" variant="ghost" size="icon" onClick={() => removeSection(section.stable_key)} title="Verwijderen"><Trash2 className="h-4 w-4 text-red-500" /></Button>
          </div>
        </header>
        <div className="structured-song-lines">
          {section.lines.map((line) => <div className="structured-song-line" key={line.stable_key}>
            <textarea data-line-key={line.stable_key} value={line.text} onFocus={() => setActiveSectionKey(section.stable_key)} onChange={(event) => updateLine(section.stable_key, line.stable_key, { text: event.target.value })} placeholder="Tekstregel…" />
            <div className="structured-song-chords" aria-label="Akkoorden">
              {line.chords.map((chord) => <button type="button" key={`${line.stable_key}-${chord.position}`} onClick={() => setEditingChord({ sectionKey: section.stable_key, lineKey: line.stable_key, position: chord.position, value: chord.name })}>{chord.name}</button>)}
              <button type="button" onClick={() => setEditingChord({ sectionKey: section.stable_key, lineKey: line.stable_key, position: null, value: 'C' })}>+ Akkoord</button>
            </div>
            {editingChord?.lineKey === line.stable_key && <div className="structured-song-chord-editor"><input autoFocus value={editingChord.value} onChange={(event) => setEditingChord({ ...editingChord, value: event.target.value })} aria-label="Akkoordnaam" /><button type="button" onClick={() => { if (editingChord.position === null) addChordAtCursor(section, line, editingChord.value); else { updateLine(section.stable_key, line.stable_key, { chords: updateLineChord(line, editingChord.position, editingChord.value).chords }); setEditingChord(null); } }}>Opslaan</button>{editingChord.position !== null && <button type="button" onClick={() => { updateLine(section.stable_key, line.stable_key, { chords: removeLineChord(line, editingChord.position).chords }); setEditingChord(null); }}>Verwijderen</button>}</div>}
          </div>)}
          {!section.lines.length && <textarea value="" onChange={(event) => updateSection(section.stable_key, { lines: [{ stable_key: `line-local-${Date.now()}`, order: 0, text: event.target.value, chords: [], preserved_fragments: [] }] })} placeholder={`Tekst van ${section.label || SECTION_LABELS[section.type] || 'dit onderdeel'}…`} />}
        </div>
        <footer><span>{section.preserved_fragments.length ? `${section.preserved_fragments.length} geavanceerde elementen behouden.` : 'Akkoorden worden structureel opgeslagen.'}</span></footer>
      </article>)}
    </div>
    <Button type="button" variant="outline" className="w-full rounded-xl" onClick={addSection}><Plus className="mr-2 h-4 w-4" /> Onderdeel toevoegen</Button>
  </div>;
};

// `compact`: verbergt de sectie-invoegknop (die {start_of_...}-directieven
// toevoegt -- zinloos/verwarrend wanneer de aanroeper de sectiestructuur al
// los beheert, zoals Songwriting Studio) en de ingebouwde live-preview (voor
// contexten die zelf al een preview van de samengestelde tekst tonen, zodat
// er niet twee keer dezelfde weergave naast elkaar staat). Standaard `false`,
// zodat de bestaande "Nieuw ChordPro-lied"-flow in WorshipPage.jsx ongewijzigd blijft.
const ChordProEditor = ({ value, onChange, initialKey, compact = false, showPreview = true, fill = false }) => {
  // In de schermvullende werkruimte op een telefoon kosten regelnummers te veel breedte; de gebruiker kan ze aanzetten.
  const [lineNumbers, setLineNumbers] = useState(() => !(fill && typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 767px)').matches));
  const [history, setHistory] = useState([value]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [sectionType, setSectionType] = useState('verse');
  const [chordRoot, setChordRoot] = useState('C');
  const [chordQuality, setChordQuality] = useState('maj');
  // OnSong-style editing is the source of truth: one ChordPro text surface,
  // with chords inserted at the cursor. The section editor remains available
  // as an optional helper for users who prefer it.
  const [editorMode, setEditorMode] = useState('advanced');
  const mirrorRef = useRef(null);
  const numbersRef = useRef(null);
  // De gekleurde laag en de regelnummers volgen de textarea exact via een verschuiving (geen eigen scrollbalk die kan
  // afwijken), ook na het typen: anders staat de zichtbare tekst ergens anders dan de cursor.
  const syncCodeScroll = () => {
    const area = textareaRef.current;
    if (!area) return;
    const shift = (node, x, y) => { if (node) node.style.transform = `translate(${-x}px, ${-y}px)`; };
    shift(mirrorRef.current, area.scrollLeft, area.scrollTop);
    shift(numbersRef.current, 0, area.scrollTop);
  };

  const textareaRef = useRef(null);
  useLayoutEffect(syncCodeScroll);
  const lines = useMemo(() => value.split('\n').length, [value]);
  const parsed = useMemo(() => parseChordPro(value || ''), [value]);
  const structuredResult = useMemo(() => parseStructuredSong(value || ''), [value]);

  const enterStructuredMode = () => {
    if (structuredResult.status === 'needs_review') {
      const confirmed = window.confirm('Dit lied bevat geavanceerde ChordPro-elementen. Ze blijven behouden, maar controleer de waarschuwingen na het bewerken. Toch doorgaan?');
      if (!confirmed) return;
    }
    setEditorMode('structured');
  };

  useEffect(() => {
    if (history[historyIndex] !== value) {
      setHistory([value]);
      setHistoryIndex(0);
    }
  // A newly opened song intentionally resets the local editor history.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const commit = (nextValue) => {
    const nextHistory = [...history.slice(0, historyIndex + 1), nextValue].slice(-100);
    setHistory(nextHistory);
    setHistoryIndex(nextHistory.length - 1);
    onChange(nextValue);
  };
  const moveHistory = (nextIndex) => {
    if (nextIndex < 0 || nextIndex >= history.length) return;
    setHistoryIndex(nextIndex);
    onChange(history[nextIndex]);
  };

  const insertAtCursor = (text, caretOffset = text.length) => {
    const textarea = textareaRef.current;
    const start = textarea ? textarea.selectionStart : value.length;
    const end = textarea ? textarea.selectionEnd : value.length;
    const nextValue = value.slice(0, start) + text + value.slice(end);
    commit(nextValue);
    const nextCaret = start + caretOffset;
    requestAnimationFrame(() => {
      if (!textareaRef.current) return;
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(nextCaret, nextCaret);
    });
  };

  const insertSection = () => {
    const label = SECTION_TYPES.find((section) => section.value === sectionType)?.label || 'Sectie';
    const needsLeadingBreak = value.length > 0 && !value.endsWith('\n\n');
    const prefix = needsLeadingBreak ? (value.endsWith('\n') ? '\n' : '\n\n') : '';
    const block = `${prefix}{start_of_${sectionType}: ${label}}\n\n{end_of_${sectionType}}\n`;
    insertAtCursor(block, prefix.length + `{start_of_${sectionType}: ${label}}\n`.length);
  };

  const insertChord = () => {
    const quality = chordQuality === 'maj' ? '' : chordQuality;
    insertAtCursor(`[${chordRoot}${quality}]`);
  };

  const updateMetadata = (name, nextValue) => {
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const directive = new RegExp(`^\\{(?:${escaped})\\s*:[^}]*\\}\\s*$`, 'im');
    const replacement = `{${name}: ${nextValue}}`;
    const nextValueSource = directive.test(value)
      ? value.replace(directive, nextValue.trim() ? replacement : '')
      : (nextValue.trim() ? `${replacement}\n${value}` : value);
    commit(nextValueSource.replace(/^\n+/, ''));
  };

  const focusSection = (section, occurrence = 0) => {
    const sourceLines = value.split('\n');
    const directiveNames = {
      chorus: ['start_of_chorus', 'soc'], verse: ['start_of_verse', 'sov'],
      bridge: ['start_of_bridge', 'sob'], intro: ['start_of_intro', 'soi'],
      outro: ['start_of_outro', 'soo'], repeat: ['start_of_repeat', 'sor'],
      tab: ['start_of_tab', 'sot'], grid: ['start_of_grid', 'sog'],
    }[section.type] || [];
    const directivePattern = new RegExp(`^\\s*\\{(?:${directiveNames.join('|')})(?::[^}]*)?\\}\\s*$`, 'i');
    const matches = sourceLines.reduce((result, line, index) => directivePattern.test(line) ? [...result, index] : result, []);
    const index = matches[occurrence] ?? -1;
    if (index < 0) return;
    const textarea = textareaRef.current;
    const offset = sourceLines.slice(0, index + 1).reduce((total, line) => total + line.length + 1, 0);
    textarea?.focus();
    textarea?.setSelectionRange(Math.min(offset, value.length), Math.min(offset, value.length));
    textarea?.scrollTo({ top: Math.max(0, index * 24 - 80), behavior: 'smooth' });
  };

  if (!compact && editorMode === 'structured') {
    return <div className="structured-song-editor-shell">
      <div className="structured-song-modebar"><span>Onderdelenweergave</span><Button type="button" variant="outline" size="sm" onClick={() => setEditorMode('advanced')}>Naar teksteditor</Button></div>
      {structuredResult.status === 'unsupported' ? <div className="p-4 text-sm text-amber-700"><p>Dit lied kan niet veilig visueel worden weergegeven. Gebruik de geavanceerde modus om de bron te controleren.</p><Button type="button" variant="outline" size="sm" className="mt-3" onClick={() => setEditorMode('advanced')}>Open geavanceerde modus</Button></div> : <StructuredSongEditor result={structuredResult} source={value} onChange={onChange} />}
    </div>;
  }

  const hasPreview = !compact && showPreview;
  const gridClass = ['chordpro-editor-grid', compact && 'chordpro-editor-grid-compact', !compact && !showPreview && 'chordpro-editor-grid-single', fill && 'chordpro-editor-grid-fill']
    .filter(Boolean).join(' ');
  return <div className={gridClass}>
    <section className="chordpro-editor-panel">
      {!compact && <div className="structured-song-modebar"><span>ChordPro-teksteditor</span><Button type="button" variant="outline" size="sm" onClick={enterStructuredMode}>Onderdelenweergave</Button></div>}
      <details className="chordpro-metadata-panel" open={!compact && !fill}>
        <summary>Songgegevens en structuur</summary>
        <div className="chordpro-metadata-fields">
          <label>Titel<input value={parsed.metadata.title || ''} onChange={(event) => updateMetadata('title', event.target.value)} placeholder="Titel van het lied" /></label>
          <label>Artiest / uitvoerder<input value={parsed.metadata.artist || ''} onChange={(event) => updateMetadata('artist', event.target.value)} placeholder="Nog niet herkend" /></label>
          <label>Copyright<input value={parsed.metadata.copyright || ''} onChange={(event) => updateMetadata('copyright', event.target.value)} placeholder="Optioneel" /></label>
        </div>
        <div className="chordpro-section-navigator" aria-label="Liedonderdelen">
          {parsed.sections.filter((section) => section.type !== 'body').map((section, index) => {
            const occurrence = parsed.sections.slice(0, parsed.sections.indexOf(section)).filter((candidate) => candidate.type === section.type).length;
            return <button type="button" key={`${section.type}-${index}`} onClick={() => focusSection(section, occurrence)}>{section.label || section.type}</button>;
          })}
          {!parsed.sections.some((section) => section.type !== 'body') && <span>Geen secties herkend — voeg Couplet/Refrein-markeringen toe.</span>}
        </div>
      </details>
      <div className="chordpro-editor-toolbar">
        <div className="flex gap-1"><Button type="button" size="icon" variant="ghost" title="Ongedaan maken" disabled={historyIndex === 0} onClick={() => moveHistory(historyIndex - 1)}><Undo2 className="h-4 w-4" /></Button><Button type="button" size="icon" variant="ghost" title="Opnieuw" disabled={historyIndex === history.length - 1} onClick={() => moveHistory(historyIndex + 1)}><Redo2 className="h-4 w-4" /></Button></div>
        <Button type="button" size="sm" variant="ghost" onClick={() => setLineNumbers((visible) => !visible)}>{lineNumbers ? <EyeOff className="mr-1 h-4 w-4" /> : <Eye className="mr-1 h-4 w-4" />}Regelnummers</Button>
      </div>
      <div className="chordpro-editor-toolbar flex-wrap">
        {!compact && (
          <div className="flex items-center gap-1">
            <Select value={sectionType} onValueChange={setSectionType}>
              <SelectTrigger className="h-8 w-40 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SECTION_TYPES.map((section) => <SelectItem key={section.value} value={section.value}>{section.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button type="button" size="sm" variant="outline" onClick={insertSection}>Sectie invoegen</Button>
          </div>
        )}
        <div className="flex items-center gap-1">
          <Select value={chordRoot} onValueChange={setChordRoot}>
            <SelectTrigger className="h-8 w-16 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CHORD_KEYS.map((key) => <SelectItem key={key} value={key}>{key}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={chordQuality} onValueChange={setChordQuality}>
            <SelectTrigger className="h-8 w-24 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CHORD_QUALITIES.map((quality) => <SelectItem key={quality.value} value={quality.value}>{quality.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button type="button" size="sm" variant="outline" onClick={insertChord} title="Voegt het akkoord in op de cursorpositie">Akkoord invoegen</Button>
        </div>
      </div>
      <div className="chordpro-quick-chord-bar" aria-label="Veelgebruikte akkoorden">
        <span>Snelle akkoorden</span>
        {(parsed.chords || []).map((chord) => <Button key={chord} type="button" size="sm" variant="outline" onMouseDown={(event) => event.preventDefault()} onClick={() => insertAtCursor(`[${chord}]`)}>{chord}</Button>)}
        <span className="chordpro-quick-chord-hint">Plaats de cursor in de tekst en kies een akkoord.</span>
      </div>
      <div className="chordpro-code-wrap">
        {lineNumbers && <div className="chordpro-line-numbers" aria-hidden="true"><div ref={numbersRef}>{Array.from({ length: lines }, (_, index) => <span key={index}>{index + 1}</span>)}</div></div>}
        <div className="chordpro-code-layer">
          <pre aria-hidden="true"><div ref={mirrorRef} className="chordpro-code-mirror"><HighlightedSource source={value} />{String.fromCharCode(10)}</div></pre>
          <textarea ref={textareaRef} aria-label="ChordPro bron" spellCheck="false" value={value} onChange={(event) => commit(event.target.value)} onScroll={syncCodeScroll} />
        </div>
      </div>
    </section>
    {hasPreview && <section className="chordpro-preview-panel"><ChordProRenderer source={value} initialKey={initialKey} /></section>}
  </div>;
};

export default ChordProEditor;
